import { NgClass } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { ActivatedRoute } from '@angular/router';
import { faEnvelope } from '@fortawesome/free-solid-svg-icons';
import { NgxSpinnerService } from 'ngx-spinner';
import { Observable } from 'rxjs';
import { ColumnType } from 'src/app/controls/advanced-gridview/models/column-type';
import { FilterInfos } from 'src/app/controls/advanced-gridview/models/filter-infos';
import { FormMode } from 'src/app/controls/common/form-mode';
import { FilterType } from 'src/app/controls/filters/models/filter-type';
import { DefaultManagementComponent } from 'src/app/controls/forms/default-management.component';
import { HttpError } from 'src/app/core/http-error';
import { RouteData } from 'src/app/core/route-data';
import { EmailAddress } from 'src/app/models/email-address';
import { EmailTemplate } from 'src/app/models/email-template';
import { Rights } from 'src/app/models/enums/rights';
import { IEmailTemplate } from 'src/app/models/interfaces/email-template.interface';
import { EmailAddressesService } from 'src/app/services/emailAddress.service';
import { EmailsTemplatesService } from 'src/app/services/emails-templates.service';
import { MemoryService } from 'src/app/services/memory.service';
import { NotificationService } from 'src/app/services/notification-service';
import { DetailHeaderComponent } from '../../controls/detail-header/detail-header.component';
import { ListHeaderComponent } from '../../controls/list-header/list-header.component';
import { EmailTemplateDetailComponent } from './email-template-detail/email-template-detail.component';
import { EmailsTemplatesDetailContext } from './emails-tempaltes-detail-context';
import { EmailsTemplatesFormContext } from './emails-tempaltes-form-context';
import { EmailsTemplatesListComponent } from './emails-templates-list/emails-templates-list.component';

enum DisplayMode {
    List,
    Form,
}

@Component({
    selector: 'app-emails-templates',
    templateUrl: './emails-templates.component.html',
    styleUrls: ['./emails-templates.component.scss'],
    imports: [MatExpansionModule, NgClass, ListHeaderComponent, EmailsTemplatesListComponent, DetailHeaderComponent, EmailTemplateDetailComponent],
})
export class EmailsTemplatesComponent extends DefaultManagementComponent<IEmailTemplate, EmailTemplate, number> implements OnInit {
    /**
     * Liste des icônes utilisés dans le template
     */
    public faEmailsTemplates = faEnvelope;

    /**
     * Liste des enums utilisés dans le template
     */
    public override FormMode = FormMode;
    public override DisplayMode = DisplayMode;

    /**
     * Contexte par défaut du formulaire contenant les données invariantes entre template de mail.
     */
    public defaultContext: EmailsTemplatesDetailContext = new EmailsTemplatesDetailContext();

    /**
     * Indique si le bouton d'ajout/Edit doit apparaitre.
     */
    public displayAddbuttom = true;
    public displayEditbuttom = true;

    /**
     * Valeur indiquant s'il s'agit de la gestion des mails globaux de la souscription.
     */
    public global: boolean;

    /**
     * Liste des adresses de l'organisation courante.
     */
    addresses: EmailAddress[];

    /**
     *
     * @param notificationService : Service utilisé pour gérer l'affichage des notifications.
     * @param emailsTemplatesService : Service métier utilisé pour gérer les clients.
     */
    constructor(
        protected override notificationService: NotificationService,
        private emailsTemplatesService: EmailsTemplatesService,
        private emailAddressesService: EmailAddressesService,
        private memoryService: MemoryService,
        private spinner: NgxSpinnerService,
        private activatedRoute: ActivatedRoute
    ) {
        super(notificationService);

        activatedRoute.data.subscribe((data: RouteData) => {
            this.global = data.global;
        });
    }

    /**
     * Appellé lors de l'initialisation du composant.
     */
    ngOnInit(): void {
        this.displayAddbuttom = !!this.memoryService.getConnectedUser().hasRight(Rights.EmailTemplateCreate);
        this.displayEditbuttom = !!this.memoryService.getConnectedUser().hasRight(Rights.EmailTemplateEdit);

        this.loadAddresses();
    }

    /**
     * Appellé lorsque l'utilisateur selectionne un template de mail.
     * @param emailTemplate template de mail selectionné.
     */
    public onEmailTemplateSelected(emailTemplate: EmailTemplate) {
        this.spinner.show('main-spinner');
        this.createContext();

        this.loadEmailTemplate(emailTemplate.id).subscribe(
            (x) => {
                this.refresh(EmailTemplate.createFormCopy(x), FormMode.Reading);
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', "Impossible d'afficher le modèle de mail." + error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }

    /**
     * Appellé lorsque l'utilisateur clique sur l'icône 'Retour à la liste'.
     */
    public onBackToList() {
        this.setDisplayMode(DisplayMode.List);
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Consulter' dans le menu contextuel.
     * @param emailTemplate template de mail sélectionné.
     */
    public onEmailTemplateDisplayed(emailTemplate: EmailTemplate) {
        this.spinner.show('main-spinner');
        this.createContext();

        this.loadEmailTemplate(emailTemplate.id).subscribe(
            (x) => {
                this.refresh(EmailTemplate.createFormCopy(x), FormMode.Reading);
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', "Impossible d'afficher le modèle de mail." + error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Editer' dans le menu contextuel.
     * @param emailTemplate template email sélectionné.
     */
    public onEmailTemplateEdited(emailTemplate: EmailTemplate) {
        this.spinner.show('main-spinner');
        this.createContext();

        // Cas de la mise à jour.
        this.loadEmailTemplate(emailTemplate.id).subscribe(
            (x) => {
                this.refresh(EmailTemplate.createFormCopy(x), FormMode.Edition);
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', "Impossible d'éditer le modèle de mail." + error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }

    /**
     * Appellé lorsque l'utilisateur clique sur le bouton 'Créer'.
     */
    public onEmailTemplateCreated() {
        this.displayForm(EmailTemplate.createFormCopy(new EmailTemplate()), FormMode.Creation);
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Dupliquer' dans le menu contextuel.
     * @param emailTemplate template de mail sélectionné.
     */
    public onEmailTemplateDuplicated(emailTemplate: EmailTemplate) {
        let duplicatedEmailTemplate = EmailTemplate.createFormCopy(emailTemplate);
        duplicatedEmailTemplate.id = null;
        this.displayForm(duplicatedEmailTemplate, FormMode.Creation);
    }

    /**
     * Appellé lorsque le contenu du formulaire a été sauvegardé.
     * @param emailTemplate : Valeur du template de mail retourné par le formulaire.
     */
    public onEmailTemplateUpdated(emailTemplate: EmailTemplate): void {
        this.spinner.show('main-spinner');
        if (this.context.editedItemId) {
            // Cas de la mise à jour.
            this.emailsTemplatesService.update(this.context.editedItemId, emailTemplate, this.global).subscribe(
                (x) => {
                    this.refreshWithNotification(EmailTemplate.createFormCopy(x), 'Modèle de mail mis à jour', FormMode.Reading);
                },
                (error: HttpError) => {
                    this.notificationService.displayError('Erreur', 'Impossible de mettre à jour le modèle de mail.' + error.message);
                    this.spinner.hide('main-spinner');
                }
            );
        } else {
            // Cas de la création.
            this.emailsTemplatesService.add(emailTemplate, this.global).subscribe(
                (x) => {
                    this.refreshWithNotification(EmailTemplate.createFormCopy(x), 'Modèle de mail créé', FormMode.Reading);
                },
                (error: HttpError) => {
                    this.notificationService.displayError('Erreur', 'Impossible de créer le modèle de mail.' + error.message);
                    this.spinner.hide('main-spinner');
                }
            );
        }
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Supprimer' dans le menu contextuel.
     * @param params Template de mail sélectionné.
     */
    public onEmailTemplateDeleted(emailTemplate: EmailTemplate) {
        this.spinner.show('main-spinner');
        // Suppression effective du client.
        this.emailsTemplatesService.delete(emailTemplate.id, this.global).subscribe(
            (x) => {
                this.notificationService.displaySuccess(null, 'Modèle de mail supprimé');
                this.itemsList.refresh();
                this.spinner.hide('main-spinner');
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', 'Impossible de supprimer le template de mail.' + error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }

    /**
     * Appellé lors de l'export
     */
    public onEmailTemplateExported() {
        this.createContext();
        this.itemsList.export('TempaltesEmails.xlsx');
    }

    /**
     * Appellé lors de l'import
     */
    public onEmailTemplateImported() {
        this.itemsList.refresh();
    }

    /**
     * Appellé lors de l'aide
     */
    public onEmailTemplateHelped() {
        this.itemsList.displayHelpDialog('emailsTemplates');
    }

    /**
     * Charge les données d'un template de mail.
     * @param emailTempalteId : Id du template de mail à charger.
     */
    public loadEmailTemplate(emailTempalteId: number): Observable<EmailTemplate> {
        return this.emailsTemplatesService.get(emailTempalteId);
    }

    /**
     * Pré-rempli les champs du formulaire.
     * @param emailTemplate : Valeur utilisée pour pré-remplir les champs du formulaire.
     */
    protected fillForm(emailTemplate: IEmailTemplate): EmailsTemplatesDetailContext {
        let context = this.createContext();
        context.formContext.item = emailTemplate;
        context.editedItemId = emailTemplate.id;
        context.global = this.global;
        return context;
    }

    /**
     * Créer un nouveau contexte.
     */
    private createContext(): EmailsTemplatesDetailContext {
        let context = new EmailsTemplatesDetailContext();
        (context.formContext as EmailsTemplatesFormContext).addresses = this.addresses;
        return context;
    }

    /**
     * Charge la liste des adresses email proposées dans la liste déroulante adresse mail du formulaire.
     */
    private loadAddresses() {
        this.spinner.show('main-spinner');

        let filters: FilterInfos[] = [];
        let filter = new FilterInfos();
        filter.ColumnName = 'sendingEnabled';
        filter.FilterType = FilterType.Boolean;
        filter.Type = ColumnType.Boolean;
        filter.Value = 'true';

        filters.push(filter);

        this.emailAddressesService.List(null, filters, null, null, 0, 1000).subscribe(
            (x) => {
                this.addresses = x.value;
                this.spinner.hide('main-spinner');
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', 'Impossible de charger la liste des adresses mail.' + error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }
}
